
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form } from 'formik';

// MUI
import {
  Box,
  Button,
  Card,
  Divider,
  Grid,
  Typography,
  styled,
  alpha,
  Chip,
  Alert
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Custom
import { BreadCrumbsAndTitle } from '../../../components/generics/BreadCrumbsAndTitle';
import { mtDividerUpTable } from '../../../commons';
import { 
    storeCreateAssignment, 
    storeUpdateAssignment,
    storeDetailAssignment,
    getDevicesStore,
    getSurveysStore,
    getAvailableConfigsRatingsStore
} from '../../../store/ratings/thunks';

// Formik Models
import { formInitialValues } from './formModels/formInitialValues';
import { validationSchema } from './formModels/validationSchema';
import { asignarEncuestaFormModel } from './formModels/asignarEncuestaFormModel';

// Hooks
import { useResponseHandler } from '../../../hooks/useResponseHandler';
import { hasPemissions } from '../../../commons/utils';

// Custom Components
import { TableAsignarEncuestas } from './TableAsignarEncuestas';
import SelectField from '../../../components/formFields/selectField';
import AutocompleteField from '../../../components/formFields/autocompleteField';
import AutocompleteMultiples from '../../../components/formFields/AutocompleteMultiple';
import { DetailAssignmentModal } from './DetailAssignmentModal';
import { AlertsInformativos } from '../../../components/generics/AlertsInformativos';
import { alertInformativo } from '../../../commons/texts';
import { BannerInfoAsignaciones } from './BannerInfoAsignaciones';
import AddIcon from '@mui/icons-material/Add';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: (theme.shape.borderRadius as number) * 2,
  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}));

const { formField } = asignarEncuestaFormModel;

export const AsignarEncuestas = () => {
  const dispatch = useDispatch();
  const { handleResponse, ResponseComponents } = useResponseHandler();

  const { devices_list, surveys_list, available_configs }: any = useSelector((state: any) => state.ratingsDevices);

  const [view, setView] = useState<'list' | 'form'>('list');
  const [editMode, setEditMode] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<any>(null);

  const [hasPermission, setHasPermission] = useState<boolean>(true);
  const [canCreate, setCanCreate] = useState<boolean>(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [detailData, setDetailData] = useState<any>(null);

  useEffect(() => {
    checkPermissions();
  }, []);

  const checkPermissions = async () => {
    const viewPerm = await hasPemissions('modulo.calificaciones', 'modulo.calificacion.asignar.encuestas', 'view');
    if (!viewPerm) setHasPermission(false);

    const createPerm = await hasPemissions('modulo.calificaciones', 'modulo.calificacion.asignar.encuestas', 'create');
    if (createPerm) setCanCreate(true);
  };

  const handleOpenForm = async (assignment: any = null) => {
    if (assignment) {
      const response = await dispatch(storeDetailAssignment(assignment.id, 'editar'));
      if (response.ok) {
          const data = response.data;
          setSelectedAssignment({
              ...data,
              deviceManagementId: data.deviceManagement?.id,
              // Pre-seleccionar solo las que ya están asignadas
              surveyId: data.surveys ? data.surveys.filter((s: any) => s.assigned === true).map((s: any) => ({ value: s.value, label: s.label })) : []
          });
          setEditMode(true);
          setView('form');
      }
    } else {
      dispatch(getAvailableConfigsRatingsStore());
      setSelectedAssignment(formInitialValues);
      setEditMode(false);
      setView('form');
    }
  };

  const handleOpenDetail = (assignment: any) => {
    setDetailData(assignment);
    setOpenDetail(true);
  };

  const handleBack = () => {
    setView('list');
    setEditMode(false);
    setSelectedAssignment(null);
  };

  const handleSubmit = async (values: any, { resetForm }: any) => {
    let response;
    const valuesToSend = {
      ...values,
      deviceManagementId: values.deviceManagementId?.value || values.deviceManagementId,
      surveyId: Array.isArray(values.surveyId) ? values.surveyId.map((s: any) => s.value || s) : values.surveyId
    };

    if (editMode) {
      response = await dispatch(storeUpdateAssignment({ ...valuesToSend, id: selectedAssignment.id }));
    } else {
      response = await dispatch(storeCreateAssignment(valuesToSend));
    }

    const actionText = editMode ? 'actualizando asignación' : 'creando asignación';
    const ok = await handleResponse(response, actionText);

    if (response.ok) {
      resetForm();
      setView('list');
    }
  };

  // if (!hasPermission) {
  //   return (
  //       <Box sx={{ p: 3 }}>
  //           <Typography variant="h5">No tienes permisos para ver este módulo.</Typography>
  //       </Box>
  //   );
  // }

  return (
    <Box sx={{ p: { xs: 2, md: 3 },  margin: '0 auto' }}>
      {ResponseComponents}
      



      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
            <BreadCrumbsAndTitle 
              title="Asignar Encuestas" 
              mainModule="calificaciones" 
              parent="Configuraciones" 
              icon="AssignmentAdd"
              action={view === 'form' ? (editMode ? 'Editar' : 'Nuevo') : 'Asignaciones'}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <AlertsInformativos type={'info'} title={'info'} content={alertInformativo.asignarEncuestas} /> 
            </Typography>
        </Box>

        {view === 'list' ? (
          canCreate && (
            <Button 
                variant="contained" 
                startIcon={<AddIcon />} 
                onClick={() => handleOpenForm()}
                sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: '0px 4px 8px -4px rgba(105, 108, 255, 0.42)'
                }}
            >
              Asignar Encuesta
            </Button>
          )
        ) : (
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBack}>
            Volver al Listado
          </Button>
        )}
      </Box>

      <Divider sx={{ mb: view === 'list' ? mtDividerUpTable : 3 }} />

      {view === 'list' ? (
        <>
          <TableAsignarEncuestas onEdit={handleOpenForm} onView={handleOpenDetail} />
          <BannerInfoAsignaciones />
        </>
      ) : (
        <StyledCard sx={{ p: 4, maxWidth: 800, mx: 'auto', mt: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight="700" color="primary">
            {editMode ? 'Editar Asignación' : 'Nueva Asignación'}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={4}>
            Seleccione el dispositivo y la encuesta que desea vincular.
          </Typography>

          <Formik
            initialValues={selectedAssignment || formInitialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ isSubmitting, values }) => (
              <Form>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12}}>
                    {!editMode && available_configs?.allDevicesAssigned && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        Todos los dispositivos actuales ya tienen asignaciones. Debes crear un nuevo dispositivo para realizar una nueva asignación o editar los existentes.
                      </Alert>
                    )}
                    {editMode ? (
                      <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #e9ecef' }}>
                        <Typography variant="subtitle2" color="textSecondary">Dispositivo</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            {values.deviceManagement?.name} <Chip label={values.deviceManagement?.code} size="small" variant="outlined" sx={{ ml: 1, fontWeight: 600 }} />
                        </Typography>
                      </Box>
                    ) : (
                      <AutocompleteField
                        name={formField.deviceManagementId.name}
                        label={formField.deviceManagementId.label}
                        options={available_configs?.devices || []}
                        fullWidth
                        disabled={available_configs?.allDevicesAssigned}
                      />
                    )}
                  </Grid>
                  <Grid size={{ xs: 12}}>
                    <AutocompleteMultiples
                      name={formField.surveyId.name}
                      label={formField.surveyId.label}
                      options={editMode 
                        ? (values.surveys || []).map((s: any) => ({ value: s.value, label: s.label }))
                        : (available_configs?.surveys || [])}
                      fullWidth
                    />
                  </Grid>

                  {editMode && values.users && values.users.length > 0 && (
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>Usuarios Asignados al Dispositivo (Solo Informativo)</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                        {values.users.map((u: any) => (
                          <Chip key={u.value} label={u.label} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </Grid>
                  )}
                  
                  <Grid size={{ xs: 12}} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting || (!editMode && available_configs?.allDevicesAssigned)}
                      startIcon={<SaveIcon />}
                      size="large"
                    >
                      {editMode ? 'Actualizar Asignación' : 'Guardar Asignación'}
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </StyledCard>
      )}

      <DetailAssignmentModal 
        open={openDetail} 
        onClose={() => setOpenDetail(false)} 
        assignment={detailData} 
      />
    </Box>

  );
};
